import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Loader2, TrendingUp, Users, Target, ClipboardCheck,
  ArrowUpRight, ArrowDownRight, Minus, BarChart3, Sparkles,
  AlertTriangle, CheckCircle, XCircle, FileText, RefreshCw, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════
// PREDICTIVE CENSUS
// ═══════════════════════════════════════════════════════
function PredictiveCensus() {
  const [snapshot, setSnapshot] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predicting, setPredicting] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/wave3/census/snapshot`)
      .then(r => r.json()).then(setSnapshot)
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const predict = async () => {
    setPredicting(true);
    try {
      const res = await fetch(`${API}/api/wave3/census/predict`);
      setPrediction(await res.json());
    } catch {}
    setPredicting(false);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-sage-400" /></div>;

  return (
    <div data-testid="predictive-census">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-sage-400" />
        <h2 className="text-lg font-heading font-bold text-[#2C3D30]">Predictive Census</h2>
        <Badge className="bg-sage-50 text-sage-400 text-xs">Analytics</Badge>
      </div>

      {snapshot && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-sage-50 rounded-xl p-3 border border-sage-100" data-testid="census-active">
              <p className="text-xs text-sage-400 font-medium">Active Census</p>
              <p className="text-2xl font-bold text-sage-800">{snapshot.current_census}</p>
            </div>
            <div className="bg-sky-50 rounded-xl p-3 border border-sky-100" data-testid="census-pipeline">
              <p className="text-xs text-sky-400 font-medium">In Pipeline</p>
              <p className="text-2xl font-bold text-sky-800">{snapshot.pipeline_total}</p>
            </div>
            <div className="bg-terra-50 rounded-xl p-3 border border-terra-100" data-testid="census-acuity">
              <p className="text-xs text-terra-400 font-medium">High Acuity</p>
              <p className="text-2xl font-bold text-terra-800">{snapshot.high_acuity_count}</p>
            </div>
            <div className="bg-sand-100 rounded-xl p-3 border border-sand-200" data-testid="census-avg-pps">
              <p className="text-xs text-[#5C6A5F] font-medium">Avg PPS</p>
              <p className="text-2xl font-heading font-bold text-[#2C3D30]">{snapshot.avg_pps_score}</p>
            </div>
          </div>

          {/* Level of Care */}
          <div className="mb-4 p-3 rounded-xl bg-sand-50 border border-sand-200">
            <p className="text-xs font-medium text-[#5C6A5F] mb-2">Level of Care Distribution</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(snapshot.level_of_care || {}).map(([loc, count]) => (
                <div key={loc} className="flex items-center gap-1.5 px-3 py-1.5 bg-sand-100 rounded-full">
                  <span className="text-xs font-medium text-[#2C3D30]">{loc}</span>
                  <Badge variant="outline" className="text-xs">{count}</Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Diagnosis Mix */}
          <div className="mb-4 p-3 rounded-xl bg-sand-50 border border-sand-200">
            <p className="text-xs font-medium text-[#5C6A5F] mb-2">Diagnosis Distribution</p>
            <div className="space-y-1.5">
              {Object.entries(snapshot.diagnosis_distribution || {}).slice(0, 8).map(([diag, count]) => (
                <div key={diag} className="flex items-center justify-between">
                  <span className="text-xs text-[#5C6A5F]">{diag}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-1.5 bg-sand-200 rounded-full overflow-hidden">
                      <div className="h-full bg-sage-400 rounded-full"
                        style={{ width: `${(count / snapshot.current_census) * 100}%` }} />
                    </div>
                    <span className="text-xs text-[#5C6A5F] w-6 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pipeline */}
          {Object.keys(snapshot.admissions_pipeline || {}).length > 0 && (
            <div className="mb-4 p-3 rounded-xl bg-sand-50 border border-sand-200">
              <p className="text-xs font-medium text-[#5C6A5F] mb-2">Admissions Pipeline</p>
              <div className="flex gap-1">
                {Object.entries(snapshot.admissions_pipeline).map(([stage, count]) => (
                  <div key={stage} className="flex-1 text-center p-2 bg-sky-50 rounded-xl">
                    <p className="text-lg font-bold text-sky-800">{count}</p>
                    <p className="text-xs text-sky-400 leading-tight">{stage.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Prediction */}
          <Button onClick={predict} disabled={predicting} className="w-full bg-sage-400 hover:bg-sage-500 mb-3"
            data-testid="census-predict-btn">
            {predicting ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Analyzing trends...</>
              : <><Sparkles className="w-4 h-4 mr-1.5" />Generate 30-Day Census Prediction</>}
          </Button>

          {prediction && (
            <Card className="border-l-4 border-l-sage-400" data-testid="census-prediction">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-sage-100 rounded-xl px-3 py-1">
                    <p className="text-xs text-sage-400">Projected 30-Day</p>
                    <p className="text-lg font-bold text-sage-800">{prediction.projected_30_day} patients</p>
                  </div>
                  {prediction.projected_30_day > snapshot.current_census
                    ? <ArrowUpRight className="w-5 h-5 text-sage-400" />
                    : prediction.projected_30_day < snapshot.current_census
                      ? <ArrowDownRight className="w-5 h-5 text-terra-400" />
                      : <Minus className="w-5 h-5 text-sand-400" />}
                </div>
                <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{prediction.ai_analysis}</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// REFERRAL CONVERSION
// ═══════════════════════════════════════════════════════
function ReferralConversion() {
  const [analytics, setAnalytics] = useState(null);
  const [optimization, setOptimization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/wave3/referrals/analytics`)
      .then(r => r.json()).then(setAnalytics)
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const optimize = async () => {
    setOptimizing(true);
    try {
      const res = await fetch(`${API}/api/wave3/referrals/optimize`);
      setOptimization(await res.json());
    } catch {}
    setOptimizing(false);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-sky-300" /></div>;

  return (
    <div data-testid="referral-conversion">
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-5 h-5 text-sky-400" />
        <h2 className="text-lg font-heading font-bold text-[#2C3D30]">Referral Conversion</h2>
        <Badge className="bg-sky-50 text-sky-500 text-xs">Optimization</Badge>
      </div>

      {analytics && (
        <>
          {/* Conversion Rate */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-sky-50 rounded-xl p-3 border border-sky-100 text-center" data-testid="referral-total">
              <p className="text-xs text-sky-400 font-medium">Total Referrals</p>
              <p className="text-2xl font-bold text-sky-800">{analytics.total_referrals}</p>
            </div>
            <div className="bg-sage-50 rounded-xl p-3 border border-sage-100 text-center" data-testid="referral-admitted">
              <p className="text-xs text-sage-600 font-medium">Admitted</p>
              <p className="text-2xl font-bold text-sage-800">{analytics.admitted}</p>
            </div>
            <div className={`rounded-xl p-3 border text-center ${analytics.conversion_rate >= 50 ? 'bg-sage-50 border-sage-100' : analytics.conversion_rate >= 30 ? 'bg-terra-50 border-terra-100' : 'bg-terra-50 border-terra-100'}`}
              data-testid="referral-rate">
              <p className="text-xs font-medium" style={{ color: analytics.conversion_rate >= 50 ? '#059669' : analytics.conversion_rate >= 30 ? '#d97706' : '#dc2626' }}>Conversion</p>
              <p className="text-2xl font-bold" style={{ color: analytics.conversion_rate >= 50 ? '#065f46' : analytics.conversion_rate >= 30 ? '#92400e' : '#991b1b' }}>{analytics.conversion_rate}%</p>
            </div>
          </div>

          {/* Funnel */}
          <div className="mb-4 p-3 rounded-xl bg-sand-50 border border-sand-200">
            <p className="text-xs font-medium text-[#5C6A5F] mb-3">Referral Funnel</p>
            <div className="space-y-2">
              {analytics.funnel.filter(f => f.count > 0).map((stage, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-[#5C6A5F] w-28 truncate">{stage.stage}</span>
                  <div className="flex-1 h-5 bg-sand-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-end pr-2 transition-all"
                      style={{ width: `${Math.max(stage.percentage, 8)}%` }}>
                      <span className="text-xs text-white font-medium">{stage.count}</span>
                    </div>
                  </div>
                  <span className="text-xs text-sand-400 w-10 text-right">{stage.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Referral Sources */}
          {Object.keys(analytics.referral_sources || {}).length > 0 && (
            <div className="mb-4 p-3 rounded-xl bg-sand-50 border border-sand-200">
              <p className="text-xs font-medium text-[#5C6A5F] mb-2">Top Referral Sources</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(analytics.referral_sources).slice(0, 6).map(([src, count]) => (
                  <Badge key={src} variant="outline" className="text-xs">{src}: {count}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* AI Optimization */}
          <Button onClick={optimize} disabled={optimizing} className="w-full bg-sky-300 hover:bg-sky-400 mb-3"
            data-testid="referral-optimize-btn">
            {optimizing ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Analyzing pipeline...</>
              : <><Sparkles className="w-4 h-4 mr-1.5" />Get AI Conversion Recommendations</>}
          </Button>

          {optimization && (
            <Card className="border-l-4 border-l-sky-300" data-testid="referral-optimization">
              <CardContent className="pt-4">
                <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{optimization.ai_recommendations}</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// SURVEY READINESS
// ═══════════════════════════════════════════════════════
function SurveyReadiness() {
  const [readiness, setReadiness] = useState(null);
  const [aiAssessment, setAiAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assessing, setAssessing] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/wave3/survey/readiness`)
      .then(r => r.json()).then(setReadiness)
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const getAiAssessment = async () => {
    setAssessing(true);
    try {
      const res = await fetch(`${API}/api/wave3/survey/ai-assessment`);
      setAiAssessment(await res.json());
    } catch {}
    setAssessing(false);
  };

  const generateReport = async () => {
    setGenerating(true);
    try { await fetch(`${API}/api/wave3/survey/generate-report`, { method: 'POST' }); }
    catch {}
    setGenerating(false);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-sky-300" /></div>;

  const statusConfig = {
    ready: { label: 'Survey Ready', color: 'bg-sage-50 text-sage-400', icon: CheckCircle },
    needs_attention: { label: 'Needs Attention', color: 'bg-terra-50 text-terra-400', icon: AlertTriangle },
    at_risk: { label: 'At Risk', color: 'bg-red-100 text-red-700', icon: XCircle },
  };

  const statusInfo = statusConfig[readiness?.status] || statusConfig.at_risk;

  return (
    <div data-testid="survey-readiness">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardCheck className="w-5 h-5 text-sky-400" />
        <h2 className="text-lg font-heading font-bold text-[#2C3D30]">Survey Readiness</h2>
        <Badge className={`text-xs ${statusInfo.color}`}>{statusInfo.label}</Badge>
      </div>

      {readiness && (
        <>
          {/* Overall Score */}
          <div className="mb-4 p-4 rounded-xl bg-sand-50 border border-sand-200 text-center" data-testid="survey-overall-score">
            <div className="relative inline-block">
              <svg className="w-24 h-24" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                <circle cx="50" cy="50" r="42" fill="none"
                  stroke={readiness.overall_score >= 80 ? '#22c55e' : readiness.overall_score >= 60 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${readiness.overall_score * 2.64} 264`}
                  transform="rotate(-90 50 50)" />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-xl font-heading font-bold text-[#2C3D30]">
                {readiness.overall_score}%
              </span>
            </div>
            <p className="text-xs text-[#5C6A5F] mt-2">{readiness.total_passed} of {readiness.total_checks} checks passed</p>
          </div>

          {/* Domains */}
          <div className="space-y-3 mb-4">
            {readiness.domains.map((domain, i) => (
              <div key={i} className="p-3 rounded-xl bg-sand-50 border border-sand-200" data-testid={`survey-domain-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#2C3D30]">{domain.domain}</span>
                  <Badge className={`text-xs ${domain.score >= 80 ? 'bg-sage-50 text-sage-400' : domain.score >= 50 ? 'bg-terra-50 text-terra-400' : 'bg-red-100 text-red-700'}`}>
                    {domain.score}%
                  </Badge>
                </div>
                <div className="space-y-1">
                  {domain.checks.map((check, j) => (
                    <div key={j} className="flex items-center gap-2 text-xs">
                      {check.status === 'pass'
                        ? <CheckCircle className="w-3.5 h-3.5 text-sage-500 flex-shrink-0" />
                        : <XCircle className="w-3.5 h-3.5 text-terra-400 flex-shrink-0" />}
                      <span className={check.status === 'pass' ? 'text-[#5C6A5F]' : 'text-terra-400'}>{check.item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-2 mb-3">
            <Button onClick={getAiAssessment} disabled={assessing} className="flex-1 bg-sky-300 hover:bg-sky-400"
              data-testid="survey-ai-btn">
              {assessing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
              AI Assessment
            </Button>
            <Button onClick={generateReport} disabled={generating} variant="outline"
              className="flex-1 border-sky-200 text-purple-700" data-testid="survey-report-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
              Save Report
            </Button>
          </div>

          {aiAssessment && (
            <Card className="border-l-4 border-l-sky-300" data-testid="survey-ai-assessment">
              <CardContent className="pt-4">
                <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{aiAssessment.ai_assessment}</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function Wave3FeaturesPage() {
  const [activeTab, setActiveTab] = useState('census');

  const tabs = [
    { id: 'census', label: 'Census Analytics', icon: TrendingUp, activeClass: 'bg-sage-50 text-sage-400 border border-sage-200' },
    { id: 'referrals', label: 'Referral Conversion', icon: Target, activeClass: 'bg-sky-50 text-sky-500 border border-sky-200' },
    { id: 'survey', label: 'Survey Readiness', icon: ClipboardCheck, activeClass: 'bg-sky-50 text-sky-500 border border-sky-200' },
  ];

  return (
    <div className="min-h-screen bg-sand-100" data-testid="wave3-page">
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-heading font-bold text-[#2C3D30]">Operations Intelligence</h1>
          <p className="text-sm text-[#5C6A5F] mt-1">Predictive analytics, conversion optimization, and compliance readiness</p>
        </div>

        <div className="flex gap-2 mb-6 border-b border-sand-200 pb-3">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeTab === tab.id ? tab.activeClass : 'text-[#5C6A5F] hover:text-[#2C3D30] hover:bg-sand-100'
              }`} data-testid={`wave3-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <Card className="shadow-sm">
          <CardContent className="p-6">
            {activeTab === 'census' && <PredictiveCensus />}
            {activeTab === 'referrals' && <ReferralConversion />}
            {activeTab === 'survey' && <SurveyReadiness />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
